// Sprint 11b.2 — Projects store.
//
// Owns the canonical list of non-archived projects ordered by sortOrder.
// Subscribes to backend `project:updated` and `projects:reordered` socket
// events so multi-tab + multi-device sync is automatic (D-007 §2.5).
//
// Actions:
//   * load()              — fetch GET /api/projects, replace state.
//   * reorder(ids)        — optimistic POST /api/projects/reorder.
//   * updateAvatar(id, p) — optimistic PATCH /api/projects/:id with avatar fields.
//
// Optimistic updates roll back on backend failure with a toast.

import { create } from 'zustand';
import { api } from '../lib/api';
import { useToastStore } from './toast.store';
import { t } from '../i18n';
import type { Project } from '../types';
import type { Socket } from 'socket.io-client';
import { useServersStore } from './servers.store';

export interface ProjectsState {
  projects: Project[];
  loading: boolean;
  error: string | null;

  load: () => Promise<void>;
  reorder: (orderedIds: string[]) => Promise<void>;
  updateAvatar: (
    id: string,
    patch: { avatarColor: string | null; avatarInitials: string | null },
  ) => Promise<void>;

  // Internal — replaces state from a backend canonical response or socket
  // event. Used by load(), reorder(), and the socket handlers.
  _setProjects: (next: Project[]) => void;
  _patchProject: (project: Project) => void;
}

function sortByOrder(list: Project[]): Project[] {
  return [...list].sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.id.localeCompare(b.id);
  });
}

function broadcastBaseUrls(): string[] {
  // Reorder + avatar updates are broadcast to every registered server in
  // parallel (D-007 §2.5). With zero registered servers we fall back to the
  // implicit local backend (empty baseUrl).
  const servers = useServersStore.getState().servers;
  if (servers.length === 0) return [''];
  return servers.map((s) => s.baseUrl);
}

export const useProjectsStore = create<ProjectsState>((set, get) => ({
  projects: [],
  loading: false,
  error: null,

  _setProjects(next) {
    set({ projects: sortByOrder(next.filter((p) => !p.archivedAt)) });
  },

  _patchProject(project) {
    set((s) => {
      const idx = s.projects.findIndex((p) => p.id === project.id);
      // Ignore archived patches in the active list — the column hides them.
      if (project.archivedAt) {
        if (idx === -1) return s;
        const next = [...s.projects];
        next.splice(idx, 1);
        return { projects: sortByOrder(next) };
      }
      if (idx === -1) {
        return { projects: sortByOrder([...s.projects, project]) };
      }
      const next = [...s.projects];
      next[idx] = project;
      return { projects: sortByOrder(next) };
    });
  },

  async load() {
    set({ loading: true, error: null });
    try {
      const res = await api<{ projects: Project[] }>('/api/projects');
      set({
        projects: sortByOrder((res.projects ?? []).filter((p) => !p.archivedAt)),
        loading: false,
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load projects';
      set({ loading: false, error: msg });
    }
  },

  async reorder(orderedIds) {
    const before = get().projects;
    if (orderedIds.length !== before.length) {
      // Reorder caller passed a different-sized list — refuse rather than
      // silently corrupt state. Reload from backend to recover.
      await get().load();
      return;
    }

    // Optimistic: rewrite local sortOrder to match the requested order with
    // 100-spacing so the visual reflects the drop immediately.
    const optimistic = orderedIds.map((id, idx) => {
      const p = before.find((x) => x.id === id);
      if (!p) return null;
      return { ...p, sortOrder: (idx + 1) * 100 };
    });
    if (optimistic.includes(null)) {
      await get().load();
      return;
    }
    set({ projects: optimistic as Project[] });

    const body = {
      order: orderedIds.map((id, idx) => ({ id, sortOrder: (idx + 1) * 100 })),
    };

    const targets = broadcastBaseUrls();
    const results = await Promise.allSettled(
      targets.map((baseUrl) =>
        api<{ ok: boolean; projects: Project[] }>('/api/projects/reorder', {
          method: 'POST',
          body,
          baseUrl: baseUrl || undefined,
        }),
      ),
    );

    const firstSuccess = results.find((r) => r.status === 'fulfilled');
    if (!firstSuccess) {
      // Total failure — revert and toast.
      set({ projects: before });
      useToastStore.getState().push({
        message: t('workspaceCustomization.reorderError'),
        durationMs: 4000,
      });
      return;
    }

    // Adopt the backend's canonical list from the first success.
    const successPayload = (firstSuccess as PromiseFulfilledResult<{
      projects: Project[];
    }>).value;
    set({
      projects: sortByOrder(successPayload.projects.filter((p) => !p.archivedAt)),
    });

    const failedCount = results.filter((r) => r.status === 'rejected').length;
    if (failedCount > 0) {
      useToastStore.getState().push({
        message: t('workspaceCustomization.syncIncomplete', { count: failedCount }),
        durationMs: 4000,
      });
    }
  },

  async updateAvatar(id, patch) {
    const before = get().projects;
    const idx = before.findIndex((p) => p.id === id);
    const current = before[idx];
    if (idx === -1 || !current) return;
    const optimisticRow: Project = {
      ...current,
      avatarColor: patch.avatarColor,
      avatarInitials: patch.avatarInitials,
    };
    const optimistic = [...before];
    optimistic[idx] = optimisticRow;
    set({ projects: optimistic });

    const targets = broadcastBaseUrls();
    const results = await Promise.allSettled(
      targets.map((baseUrl) =>
        api<{ project: Project }>(`/api/projects/${encodeURIComponent(id)}`, {
          method: 'PATCH',
          body: patch,
          baseUrl: baseUrl || undefined,
        }),
      ),
    );

    const firstSuccess = results.find((r) => r.status === 'fulfilled');
    if (!firstSuccess) {
      set({ projects: before });
      useToastStore.getState().push({
        message: t('workspaceCustomization.updateError'),
        durationMs: 4000,
      });
      return;
    }

    const project = (firstSuccess as PromiseFulfilledResult<{ project: Project }>)
      .value.project;
    get()._patchProject(project);

    const failedCount = results.filter((r) => r.status === 'rejected').length;
    if (failedCount > 0) {
      useToastStore.getState().push({
        message: t('workspaceCustomization.syncIncomplete', { count: failedCount }),
        durationMs: 4000,
      });
    }
  },
}));

// ────────────────────────────────────────────────────────────────────
// Socket subscription. Wires the active server's connection to the store
// so `project:updated` and `projects:reordered` events from any tab /
// device drop the local state into sync without user action.
// Returns an unsubscribe so DashboardLayout (or any consumer) can clean
// up on unmount / server switch.
// ────────────────────────────────────────────────────────────────────

interface UpdatedPayload {
  project: Project;
}
interface ReorderedPayload {
  projects: Project[];
}

export function subscribeProjectsToSocket(socket: Socket): () => void {
  const onUpdated = (p: UpdatedPayload) => {
    if (p?.project) useProjectsStore.getState()._patchProject(p.project);
  };
  const onReordered = (p: ReorderedPayload) => {
    if (p?.projects) useProjectsStore.getState()._setProjects(p.projects);
  };
  socket.on('project:updated', onUpdated);
  socket.on('projects:reordered', onReordered);
  return () => {
    socket.off('project:updated', onUpdated);
    socket.off('projects:reordered', onReordered);
  };
}
